<script lang="ts">
  import type { Snippet } from 'svelte';
  import { t } from '../../i18n/index.ts';

  // HealthCard is one measure's card on Health (spec 007 FR-018), laid
  // out as a title, a label such as TODAY or AVERAGE over
  // the headline, a Typical chip, a figure on the right, a chart, and a row
  // of details. The card is tinted with its measure's color, not filled, so
  // text keeps the theme's contrast.
  interface Props {
    title: string;
    icon: string;
    color: string;
    label?: string;
    value?: string;
    unit?: string;
    typical?: string;
    side?: { label: string; value: string };
    details?: { label: string; value: string }[];
    // wide spans both columns of the page's grid.
    wide?: boolean;
    children?: Snippet;
  }
  let { title, icon, color, label, value, unit, typical, side, details = [], wide = false, children }: Props = $props();

  const id = $props.id();
</script>

<section class="card overflow-hidden {wide ? 'md:col-span-2' : ''}" style="background: color-mix(in srgb, {color} 10%, var(--theme-card-bg)); border-color: color-mix(in srgb, {color} 30%, var(--theme-card-border));"
  aria-labelledby={id}>
  <div class="p-4 pb-3 flex items-start gap-4">
    <div class="flex-1 min-w-0">
      <h2 {id} class="font-semibold flex items-center gap-2"><i aria-hidden="true" class="las {icon} text-xl" style="color: {color};"></i>{title}</h2>
      {#if value}
        {#if label}<p class="text-xs uppercase tracking-wide text-text-muted mt-1">{label}</p>{/if}
        <p class="text-3xl font-semibold leading-tight">{value}{#if unit}<span class="text-sm font-normal text-text-muted ml-1">{unit}</span>{/if}</p>
      {/if}
      {#if typical}
        <p class="inline-block mt-2 px-2 py-0.5 rounded text-xs font-semibold"
          style="background: color-mix(in srgb, {color} 22%, transparent); color: var(--theme-text);"><span class="uppercase tracking-wide">{t('healthTypical')}</span> {typical}</p>
      {/if}
    </div>
    {#if side}
      <div class="text-right shrink-0 self-end">
        <p class="text-xs uppercase tracking-wide text-text-muted">{side.label}</p>
        <p class="text-2xl font-semibold leading-tight">{side.value}</p>
      </div>
    {/if}
  </div>
  {#if children}
    <div class="px-4 pb-3">{@render children()}</div>
  {/if}
  {#if details.length}
    <dl class="grid gap-2 px-4 py-3 text-center" style="grid-template-columns: repeat({details.length}, minmax(0, 1fr)); background: color-mix(in srgb, {color} 8%, transparent);">
      {#each details as d (d.label)}
        <div class="flex flex-col-reverse">
          <dt class="text-xs text-text-muted">{d.label}</dt>
          <dd class="font-semibold">{d.value}</dd>
        </div>
      {/each}
    </dl>
  {/if}
</section>
